import { useDepartmentText } from 'model/DepartmentTerminology';
import { Group, LoadingOverlay, Modal, Stack } from '@mantine/core';
import { useApi } from 'api/api-context';
import BrainItMoreInfo from 'components/BrainItMoreInfo';
import Button from 'components/Button';
import KepMaintenanceNotice from 'components/modals/kep-maintenance/KepMaintenanceNotice';
import P1Regular from 'components/typography/P1Regular';
import { FEATURE_TOGGLE_KEP_MAINTENANCE } from 'env';
import panic from 'errors/panic';
import { useEffect, useState, useMemo } from 'react';
import DepartmentGroupCard, { RevisionGroup } from 'components/modals/bulk-sign-revision-modal/DepartmentGroupCard';
import { RevisionListResponse } from 'api/actions/revision-list/revision-list-response';

export interface BulkSignRevisionModalProps {
  revisionIds: number[];
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal to bulk sign revisions grouped by department and kepMessageUuid.
 */
export default function BulkSignRevisionModal({ revisionIds, opened, onClose }: BulkSignRevisionModalProps) {
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const [loading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [revisions, setRevisions] = useState<RevisionListResponse>([]);
  const [revisionKind, setRevisionKind] = useState<'revision' | 'form'>('revision');
  const isKepMaintenanceMode = FEATURE_TOGGLE_KEP_MAINTENANCE;
  const isBusy = !isKepMaintenanceMode && fetching;

  const groupedRevisions: RevisionGroup[] = useMemo(() => {
    const departmentMap = new Map<number, RevisionGroup>();

    revisions.forEach((revision) => {
      const { departmentId, departmentName } = revision.department;

      if (!departmentMap.has(departmentId)) {
        departmentMap.set(departmentId, {
          departmentId,
          departmentName,
          groups: [],
        });
      }

      const deptGroup = departmentMap.get(departmentId)!;
      const kepKey = revision.kepMessageUuid || 'undefined';

      let kepGroup = deptGroup.groups.find((g) => (g.kepMessageUuid || 'undefined') === kepKey);

      if (!kepGroup) {
        kepGroup = {
          kepMessageUuid: revision.kepMessageUuid,
          revisions: [],
        };
        deptGroup.groups.push(kepGroup);
      }

      kepGroup.revisions.push({
        revisionId: revision.revisionId,
        revisionName: revision.revisionName,
        kepMessageUuid: revision.kepMessageUuid,
        kind: revision.kind,
      });
    });

    return Array.from(departmentMap.values());
  }, [revisions]);

  useEffect(() => {
    if (opened && revisionIds.length > 0) {
      setFetching(true);

      const revisionGetListAction = getAction('RevisionList');

      // Fetch all selected revisions first
      revisionGetListAction({ query: { filters: { 'revisionId.in': revisionIds } } })
        .then(async (selectedRevisions) => {
          // Find all unique kepMessageUuids that are defined
          const kepMessageUuids = Array.from(
            new Set(
              selectedRevisions
                .map((rev) => rev.kepMessageUuid)
                .filter((uuid): uuid is string => uuid !== undefined && uuid !== null)
            )
          );

          // If there are any kepMessageUuids, fetch all revisions with those UUIDs
          if (kepMessageUuids.length > 0) {
            const allRevisionsWithKep = await revisionGetListAction({
              query: { filters: { 'kepMessageUuid.in': kepMessageUuids } },
            });

            // Merge selected revisions with all revisions having the same kepMessageUuid
            // Use a Map to avoid duplicates
            const revisionMap = new Map<number, (typeof selectedRevisions)[0]>();

            // Add all selected revisions first
            selectedRevisions.forEach((rev) => {
              revisionMap.set(rev.revisionId, rev);
            });

            // Add all revisions with matching kepMessageUuid
            allRevisionsWithKep.forEach((rev) => {
              revisionMap.set(rev.revisionId, rev);
            });

            setRevisions(Array.from(revisionMap.values()));
          } else {
            // If no kepMessageUuids, just use the selected revisions
            setRevisions(selectedRevisions);
          }

          if (selectedRevisions.length > 0) {
            setRevisionKind(selectedRevisions[0].kind === 'revision' ? 'revision' : 'form');
          }
        })
        .catch(panic)
        .finally(() => setFetching(false));
    }
  }, [opened, revisionIds, getAction]);

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={revisionKind === 'revision' ? 'Hromadné podpísanie revíznych správ' : 'Hromadné podpísanie formulárov'}
      size="xl"
      zIndex={1000}
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      closeButtonProps={{ loading: isBusy }}
    >
      <LoadingOverlay visible={isBusy} loaderProps={{ size: 'md' }} zIndex={1000} />
      {isKepMaintenanceMode ? (
        <Stack spacing={24}>
          <KepMaintenanceNotice />
        </Stack>
      ) : (
        <Stack spacing={24}>
          <P1Regular>
            {revisionKind === 'revision'
              ? departmentText(
                  'Revízne správy sú zoskupené podľa položky úrovne a stavu podpisovania. Pri podpisovaní nie je možné kombinovať správy z rôznych položiek úrovne ani spájať predošlé hromadné podpisy.',
                  'Revízne správy sú zoskupené podľa strediska a stavu podpisovania. Pri podpisovaní nie je možné kombinovať správy z rôznych stredísk ani spájať predošlé hromadné podpisy.'
                )
              : departmentText(
                  'Formuláre sú zoskupené podľa položky úrovne a stavu podpisovania. Pri podpisovaní nie je možné kombinovať formuláre z rôznych položiek úrovne ani spájať predošlé hromadné podpisy.',
                  'Formuláre sú zoskupené podľa strediska a stavu podpisovania. Pri podpisovaní nie je možné kombinovať formuláre z rôznych stredísk ani spájať predošlé hromadné podpisy.'
                )}
          </P1Regular>
          <Stack spacing={16}>
            {groupedRevisions.map((departmentGroup) => (
              <DepartmentGroupCard key={departmentGroup.departmentId} group={departmentGroup} />
            ))}
          </Stack>
          {!loading && (
            <Group position="apart">
              <BrainItMoreInfo />
              <Group position="right">
                <Button variant="subtle-gray" onClick={onClose}>
                  Zrušiť
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      )}
    </Modal>
  );
}
